import { apiClient } from './client';
import { SourceStory, SourceChapter, SourceProcess, PaginatedResponse, SourceCharacter, SourceCharacterCheckpoint, ExtractedCharacter, SourceScene, ExtractedScene, SourcePanel, ExtractedPanel, SourceImage, SourceImageGeneration, StoryVoice, SourcePanelAudio, SourceGlossary, OutlineReviewAudienceResult, ChapterStatusMap, SceneOutfit, SourceChapterSuggestion } from '../../types';
import { SampleStoryVoice } from '../../pages/source-stories/OverviewTab';

export interface CreateSourceStoryRequest {
  name: string;
  author?: string;
  link?: string;
  storyConcept?: string;
  targetAudiences?: any[];
}

export interface UpdateSourceStoryRequest extends Partial<CreateSourceStoryRequest> {
  summary?: string;
  pros?: string;
  cons?: string;
  hook?: string;
  caption?: string;
  storyConcept?: string;
  targetAudiences?: any[];
  imageStyle?: string;
}

export interface GetSourceStoriesParams {
  pageIndex?: number;
  pageSize?: number;
  name?: string;
  /** Admin username (matches story `createdBy`) */
  createdBy?: string;
  /** Comma-separated story IDs to pin at top (client sends from localStorage; max 50 on server) */
  pinnedIds?: string;
}

export interface SourceStoriesListResponse {
  pinned: SourceStory[];
  data: SourceStory[];
  totalRecord: number;
}

export interface CreateSourceChapterRequest {
  name?: string;
  content?: string;
  link?: string;
  instruction?: string;
  outline?: string;
  orderIndex?: number;
}

export interface UpdateSourceChapterRequest extends Partial<CreateSourceChapterRequest> {}

export interface CreateSourceProcessRequest {
  name: string;
  chapterIds: string[];
  videoForm?: 'LONG' | 'SHORT';
  parentProcessId?: string;
}

export interface UpdateSourceProcessRequest {
  name?: string;
  status?: string;
  summary?: string;
  videoForm?: 'LONG' | 'SHORT';
}

export interface CreateSourceCharacterRequest {
  name: string;
  description?: string;
  role?: string;
  avatarUrl?: string;
  localizedData?: any;
  processId?: string;
}

export interface UpdateSourceCharacterRequest extends Partial<CreateSourceCharacterRequest> {
  localizedData?: any;
}

export interface CreateSourceCharacterCheckpointRequest {
  name: string;
  description?: string;
  visualPrompt?: string;
  avatarUrl?: string;
}

export interface UpdateSourceCharacterCheckpointRequest extends Partial<CreateSourceCharacterCheckpointRequest> {}

export interface SaveScenesRequest {
  scenes: Array<{
    title: string;
    summary?: string;
    setting?: string;
    characterIds?: string[];
    content?: string;
    reason?: string;
  }>;
}

export interface UpdateSourceSceneRequest {
  orderIndex?: number;
  title?: string;
  summary?: string;
  setting?: string;
  characterIds?: string[];
  content?: string;
  reason?: string;
  keyMoments?: string[];
}

export interface SavePanelsRequest {
  processId: string;
  panels: ExtractedPanel[];
}

export interface UpdateSourcePanelRequest {
  caption?: string;
  localizedCaption?: string;
  visualDescription?: string;
  refinedVisualDescription?: string;
  technicalPrompt?: string;
  motionPrompt?: string;
  characterIds?: string[];
}

export interface CreateSourceGlossaryRequest {
  originalTerm: string;
  localizedTerm: string;
  type: 'LOCATION' | 'ORGANIZATION' | 'ITEM' | 'CONCEPT' | 'OTHER';
  description?: string;
}

export interface UpdateSourceGlossaryRequest extends Partial<CreateSourceGlossaryRequest> {}

export interface GetSourceGlossaryParams {
  pageIndex?: number;
  pageSize?: number;
  originalTerm?: string;
  type?: string;
}

export const sourceStoriesApi = {
  // Source Story APIs
  getSourceStories: async (params?: GetSourceStoriesParams): Promise<SourceStoriesListResponse> => {
    return apiClient.get<SourceStoriesListResponse>('/api/v1/admin/source-stories', { params });
  },

  getSourceStoryDetail: async (id: string): Promise<{ data: { story: SourceStory; storyVoices: StoryVoice[], audios: SourcePanelAudio[] } }> => {
    return apiClient.get<{ data: { story: SourceStory; storyVoices: StoryVoice[], audios: SourcePanelAudio[] } }>(`/api/v1/admin/source-stories/${id}`);
  },

  createSourceStory: async (data: CreateSourceStoryRequest): Promise<{ data: SourceStory }> => {
    return apiClient.post<{ data: SourceStory }>('/api/v1/admin/source-stories', data);
  },

  updateSourceStory: async (id: string, data: UpdateSourceStoryRequest): Promise<{ data: SourceStory }> => {
    return apiClient.put<{ data: SourceStory }>(`/api/v1/admin/source-stories/${id}`, data);
  },

  deleteSourceStory: async (id: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${id}`);
  },

  // Content Generation APIs
  generatePhase1: async (storyId: string): Promise<{ data: SourceStory }> => {
    return apiClient.post<{ data: SourceStory }>(`/api/v1/admin/source-stories/${storyId}/overview/generate-phase1`);
  },

  generateStorytellers: async (storyId: string): Promise<{ data: { success: true } }> => {
    return apiClient.post<{ data: { success: true } }>(`/api/v1/admin/source-stories/${storyId}/overview/generate-storytellers`);
  },

  generateHook: async (storyId: string): Promise<{ data: SourceStory }> => {
    return apiClient.post<{ data: SourceStory }>(`/api/v1/admin/source-stories/${storyId}/overview/generate-hook`);
  },

  generateCaption: async (storyId: string): Promise<{ data: SourceStory }> => {
    return apiClient.post<{ data: SourceStory }>(`/api/v1/admin/source-stories/${storyId}/overview/generate-caption`);
  },

  suggestTargetAudiences: async (storyConcept: string): Promise<{ data: Array<{ code: string; name: string; description: string }> }> => {
    return apiClient.post<{ data: Array<{ code: string; name: string; description: string }> }>('/api/v1/admin/source-stories/suggest-audiences', { storyConcept });
  },

  // Source Chapter APIs
  getSourceChapters: async (storyId: string, params?: { pageIndex?: number; pageSize?: number }): Promise<PaginatedResponse<SourceChapter>> => {
    return apiClient.get<PaginatedResponse<SourceChapter>>(`/api/v1/admin/source-stories/${storyId}/chapters`, { params });
  },

  getSourceChapterDetail: async (storyId: string, chapterId: string): Promise<{ data: SourceChapter }> => {
    return apiClient.get<{ data: SourceChapter }>(`/api/v1/admin/source-stories/${storyId}/chapters/${chapterId}`);
  },

  createSourceChapter: async (storyId: string, data: CreateSourceChapterRequest): Promise<{ data: SourceChapter }> => {
    return apiClient.post<{ data: SourceChapter }>(`/api/v1/admin/source-stories/${storyId}/chapters`, data);
  },

  updateSourceChapter: async (storyId: string, chapterId: string, data: UpdateSourceChapterRequest): Promise<{ data: SourceChapter }> => {
    return apiClient.put<{ data: SourceChapter }>(`/api/v1/admin/source-stories/${storyId}/chapters/${chapterId}`, data);
  },

  deleteSourceChapter: async (storyId: string, chapterId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/chapters/${chapterId}`);
  },

  getChapterStatuses: async (storyId: string, chapterIds: string[]): Promise<{ data: ChapterStatusMap }> => {
    return apiClient.get<{ data: ChapterStatusMap }>(`/api/v1/admin/source-stories/${storyId}/chapters/status`, {
      params: { chapterIds: chapterIds.join(',') }
    });
  },

  generateChapterOutline: async (storyId: string, instruction: string, currentChapterId?: string): Promise<{ data: { outline: string } }> => {
    return apiClient.post<{ data: { outline: string } }>(`/api/v1/admin/source-stories/${storyId}/chapters/generate-outline`, { instruction, currentChapterId });
  },

  generateChapterContent: async (storyId: string, instruction: string, outline: string): Promise<{ data: { content: string } }> => {
    return apiClient.post<{ data: { content: string } }>(`/api/v1/admin/source-stories/${storyId}/chapters/generate-content`, { instruction, outline });
  },

  reviewChapter: async (storyId: string, chapterId: string, instruction: string, outline: string, content: string): Promise<{ data: any[] }> => {
    return apiClient.post<{ data: any[] }>(`/api/v1/admin/source-stories/${storyId}/chapters/${chapterId}/review`, { instruction, outline, content });
  },

  rewriteChapter: async (storyId: string, chapterId: string, instruction: string, outline: string, content: string, feedbacks: string[]): Promise<{ data: { content: string } }> => {
    return apiClient.post<{ data: { content: string } }>(`/api/v1/admin/source-stories/${storyId}/chapters/${chapterId}/rewrite`, { instruction, outline, content, feedbacks });
  },

  reviewChapterOutline: async (storyId: string, chapterId: string, instruction: string, outline: string): Promise<{ data: OutlineReviewAudienceResult[] }> => {
    return apiClient.post<{ data: OutlineReviewAudienceResult[] }>(`/api/v1/admin/source-stories/${storyId}/chapters/${chapterId}/review-outline`, { instruction, outline });
  },

  regenChapterOutline: async (storyId: string, chapterId: string, instruction: string, outline: string, feedbacks: string[]): Promise<{ data: { outline: string } }> => {
    return apiClient.post<{ data: { outline: string } }>(`/api/v1/admin/source-stories/${storyId}/chapters/${chapterId}/regen-outline`, { instruction, outline, feedbacks });
  },

  generateChapterSummary: async (storyId: string, chapterId: string): Promise<{ data: SourceChapter }> => {
    return apiClient.post<{ data: SourceChapter }>(`/api/v1/admin/source-stories/${storyId}/chapters/${chapterId}/generate-summary`);
  },

  getChapterSuggestions: async (storyId: string): Promise<{ data: SourceChapterSuggestion[] }> => {
    return apiClient.get<{ data: SourceChapterSuggestion[] }>(`/api/v1/admin/source-stories/${storyId}/chapters/suggestions`);
  },

  generateChapterSuggestions: async (storyId: string, payload: { count?: number; direction?: string }): Promise<{ data: SourceChapterSuggestion[] }> => {
    return apiClient.post<{ data: SourceChapterSuggestion[] }>(`/api/v1/admin/source-stories/${storyId}/chapters/suggestions/generate`, payload);
  },

  updateChapterSuggestion: async (storyId: string, suggestionId: string, payload: { title?: string; instruction?: string; status?: string }): Promise<{ data: SourceChapterSuggestion }> => {
    return apiClient.patch<{ data: SourceChapterSuggestion }>(`/api/v1/admin/source-stories/${storyId}/chapters/suggestions/${suggestionId}`, payload);
  },

  deleteChapterSuggestion: async (storyId: string, suggestionId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/chapters/suggestions/${suggestionId}`);
  },

  // Source Process APIs
  getSourceProcesses: async (storyId: string): Promise<{ data: SourceProcess[] }> => {
    return apiClient.get<{ data: SourceProcess[] }>(`/api/v1/admin/source-stories/${storyId}/processes`);
  },

  getSourceProcessDetail: async (storyId: string, processId: string): Promise<{ data: SourceProcess }> => {
    return apiClient.get<{ data: SourceProcess }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}`);
  },

  createSourceProcess: async (storyId: string, data: CreateSourceProcessRequest): Promise<{ data: SourceProcess }> => {
    return apiClient.post<{ data: SourceProcess }>(`/api/v1/admin/source-stories/${storyId}/processes`, data);
  },

  updateSourceProcess: async (storyId: string, processId: string, data: UpdateSourceProcessRequest): Promise<{ data: SourceProcess }> => {
    return apiClient.put<{ data: SourceProcess }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}`, data);
  },

  deleteSourceProcess: async (storyId: string, processId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}`);
  },

  cloneShortProcess: async (storyId: string, processId: string): Promise<{ data: SourceProcess }> => {
    return apiClient.post<{ data: SourceProcess }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}/clone-short`);
  },

  // Source Character APIs
  getSourceCharacters: async (storyId: string, params?: { processId?: string; pageIndex?: number; pageSize?: number }): Promise<PaginatedResponse<SourceCharacter>> => {
    return apiClient.get<PaginatedResponse<SourceCharacter>>(`/api/v1/admin/source-stories/${storyId}/characters`, { params });
  },

  createSourceCharacter: async (storyId: string, data: CreateSourceCharacterRequest): Promise<{ data: SourceCharacter }> => {
    return apiClient.post<{ data: SourceCharacter }>(`/api/v1/admin/source-stories/${storyId}/characters`, data);
  },

  updateSourceCharacter: async (storyId: string, characterId: string, data: UpdateSourceCharacterRequest): Promise<{ data: SourceCharacter }> => {
    return apiClient.put<{ data: SourceCharacter }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}`, data);
  },

  deleteSourceCharacter: async (storyId: string, characterId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}`);
  },

  // Source Character Checkpoint APIs
  getSourceCharacterCheckpoints: async (storyId: string, characterId: string, params?: { pageIndex?: number; pageSize?: number }): Promise<PaginatedResponse<SourceCharacterCheckpoint>> => {
    return apiClient.get<PaginatedResponse<SourceCharacterCheckpoint>>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/checkpoints`, { params });
  },

  createSourceCharacterCheckpoint: async (storyId: string, characterId: string, data: CreateSourceCharacterCheckpointRequest): Promise<{ data: SourceCharacterCheckpoint }> => {
    return apiClient.post<{ data: SourceCharacterCheckpoint }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/checkpoints`, data);
  },

  updateSourceCharacterCheckpoint: async (storyId: string, characterId: string, checkpointId: string, data: UpdateSourceCharacterCheckpointRequest): Promise<{ data: SourceCharacterCheckpoint }> => {
    return apiClient.put<{ data: SourceCharacterCheckpoint }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/checkpoints/${checkpointId}`, data);
  },

  deleteSourceCharacterCheckpoint: async (storyId: string, characterId: string, checkpointId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/checkpoints/${checkpointId}`);
  },

  getAllCharacterCheckpoints: async (storyId: string): Promise<{ data: SourceCharacterCheckpoint[] }> => {
    return apiClient.get<{ data: SourceCharacterCheckpoint[] }>(`/api/v1/admin/source-stories/${storyId}/checkpoints`);
  },

  extractCharacters: async (storyId: string, processId: string): Promise<{ data: ExtractedCharacter[] }> => {
    return apiClient.post<{ data: ExtractedCharacter[] }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}/extract-characters`);
  },

  saveExtractedCharacters: async (storyId: string, processId: string, data: { characters: ExtractedCharacter[] }): Promise<{ data: { success: true; characterIds: string[] } }> => {
    return apiClient.post<{ data: { success: true; characterIds: string[] } }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}/characters/bulk`, data);
  },

  // Source Scene APIs
  extractScenes: async (storyId: string, processId: string): Promise<{ data: ExtractedScene[] }> => {
    return apiClient.post<{ data: ExtractedScene[] }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}/extract-scenes`);
  },

  saveProcessScenes: async (storyId: string, processId: string, data: SaveScenesRequest): Promise<{ data: SourceScene[] }> => {
    return apiClient.post<{ data: SourceScene[] }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}/scenes`, data);
  },

  getProcessScenes: async (storyId: string, processId: string, params?: { pageIndex?: number; pageSize?: number }): Promise<PaginatedResponse<SourceScene>> => {
    return apiClient.get<PaginatedResponse<SourceScene>>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}/scenes`, { params });
  },

  updateSourceScene: async (storyId: string, processId: string, sceneId: string, data: UpdateSourceSceneRequest): Promise<{ data: SourceScene }> => {
    return apiClient.put<{ data: SourceScene }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}/scenes/${sceneId}`, data);
  },

  deleteSourceScene: async (storyId: string, processId: string, sceneId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}/scenes/${sceneId}`);
  },

  clearSourceScenes: async (storyId: string, processId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}/scenes`);
  },

  generateSceneOutfit: async (storyId: string, sceneId: string, characterId: string): Promise<{ data: SceneOutfit }> => {
    return apiClient.post<{ data: SceneOutfit }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/outfits/${characterId}/generate`);
  },

  selectSceneOutfit: async (storyId: string, sceneId: string, characterId: string, imageUrl: string): Promise<{ data: SceneOutfit }> => {
    return apiClient.post<{ data: SceneOutfit }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/outfits/${characterId}/select`, { imageUrl });
  },

  approveProcess: async (storyId: string, processId: string): Promise<{ data: SourceProcess }> => {
    return apiClient.post<{ data: SourceProcess }>(`/api/v1/admin/source-stories/${storyId}/processes/${processId}/approve`);
  },

  // Source Panel APIs
  getSourcePanels: async (storyId: string, sceneId: string): Promise<{ data: SourcePanel[] }> => {
    return apiClient.get<{ data: SourcePanel[] }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels`);
  },

  draftPanels: async (storyId: string, sceneId: string): Promise<{ data: ExtractedPanel[] }> => {
    return apiClient.post<{ data: ExtractedPanel[] }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/draft-panels`);
  },

  planSceneVisuals: async (storyId: string, sceneId: string, mediaStrategy: string): Promise<{ data: SourcePanel[] }> => {
    return apiClient.post<{ data: SourcePanel[] }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/plan-visuals`, { mediaStrategy });
  },

  saveExtractedPanels: async (storyId: string, sceneId: string, data: SavePanelsRequest): Promise<{ data: SourcePanel[] }> => {
    return apiClient.post<{ data: SourcePanel[] }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/bulk`, data);
  },

  updateSourcePanel: async (storyId: string, sceneId: string, panelId: string, data: UpdateSourcePanelRequest): Promise<{ data: SourcePanel }> => {
    return apiClient.put<{ data: SourcePanel }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}`, data);
  },

  deleteSourcePanel: async (storyId: string, sceneId: string, panelId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}`);
  },

  aiRefinePanel: async (storyId: string, sceneId: string, panelId: string, comment: string, mediaStrategy: 'AUTO' | 'IMAGE' | 'VIDEO' = 'AUTO'): Promise<{ data: SourcePanel }> => {
    return apiClient.post<{ data: SourcePanel }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/ai-refine`, { comment, mediaStrategy });
  },

  previewReDraftPanels: async (storyId: string, sceneId: string, comment: string): Promise<{ data: ExtractedPanel[] }> => {
    return apiClient.post<{ data: ExtractedPanel[] }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/re-draft-panels/preview`, { comment });
  },

  confirmReDraftPanels: async (storyId: string, sceneId: string, panels: ExtractedPanel[]): Promise<{ data: { count: number } }> => {
    return apiClient.post<{ data: { count: number } }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/re-draft-panels/confirm`, { panels });
  },

  reviewSceneContent: async (storyId: string, sceneId: string): Promise<{ data: { critiques: string[]; issues: Array<{ tag: string; description: string }>; suggestedComment: string } }> => {
    return apiClient.post<{ data: { critiques: string[]; issues: Array<{ tag: string; description: string }>; suggestedComment: string } }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/review`);
  },

  clearSourcePanels: async (storyId: string, sceneId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels`);
  },

  getAllSourcePanels: async (storyId: string): Promise<{ data: SourcePanel[] }> => {
    return apiClient.get<{ data: SourcePanel[] }>(`/api/v1/admin/source-stories/${storyId}/panels/all`);
  },

  getAllSourceScenes: async (storyId: string): Promise<{ data: SourceScene[] }> => {
    return apiClient.get<{ data: SourceScene[] }>(`/api/v1/admin/source-stories/${storyId}/scenes/all`);
  },

  getAllPanelAudios: async (storyId: string): Promise<{ data: SourcePanelAudio[] }> => {
    return apiClient.get<{ data: SourcePanelAudio[] }>(`/api/v1/admin/source-stories/${storyId}/panels/audios/all`);
  },

  aiLocalizeScenePanels: async (storyId: string, sceneId: string): Promise<{ data: Array<{ panelId: string; localizedCaption: string }> }> => {
    return apiClient.post<{ data: Array<{ panelId: string; localizedCaption: string }> }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/localize`);
  },

  // Character Image APIs
  generateCharacterVisualPrompt: async (storyId: string, characterId: string, additionalInfo?: string, checkpointId?: string): Promise<{ data: { prompt: string } }> => {
    return apiClient.post<{ data: { prompt: string } }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/visual-prompt`, { additionalInfo, checkpointId });
  },

  // Checkpoint Image APIs
  generateCheckpointImage: async (storyId: string, checkpointId: string, prompt: string): Promise<{ data: { generation: SourceImageGeneration; image: SourceImage } }> => {
    return apiClient.post<{ data: { generation: SourceImageGeneration; image: SourceImage } }>(`/api/v1/admin/source-stories/${storyId}/checkpoints/${checkpointId}/images`, { prompt });
  },

  getCheckpointImages: async (storyId: string, checkpointId: string): Promise<{ data: SourceImage[] }> => {
    return apiClient.get<{ data: SourceImage[] }>(`/api/v1/admin/source-stories/${storyId}/checkpoints/${checkpointId}/images`);
  },

  setCheckpointAvatar: async (storyId: string, checkpointId: string, imageId: string): Promise<{ data: SourceCharacterCheckpoint }> => {
    return apiClient.put<{ data: SourceCharacterCheckpoint }>(`/api/v1/admin/source-stories/${storyId}/checkpoints/${checkpointId}/avatar`, { imageId });
  },

  uploadCheckpointImage: async (storyId: string, checkpointId: string, file: File): Promise<{ data: { image: SourceImage; checkpoint: SourceCharacterCheckpoint } }> => {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.upload<{ data: { image: SourceImage; checkpoint: SourceCharacterCheckpoint } }>(`/api/v1/admin/source-stories/${storyId}/checkpoints/${checkpointId}/images/upload`, formData);
  },

  getCheckpointGenerations: async (storyId: string, checkpointId: string): Promise<{ data: SourceImageGeneration[] }> => {
    return apiClient.get<{ data: SourceImageGeneration[] }>(`/api/v1/admin/source-stories/${storyId}/checkpoints/${checkpointId}/generations`);
  },

  // Legacy/Character Image APIs (Direct character image management is being deprecated)
  generateCharacterImage: async (storyId: string, characterId: string, prompt: string, checkpointId?: string): Promise<{ data: { generation: SourceImageGeneration; image: SourceImage } }> => {
    return apiClient.post<{ data: { generation: SourceImageGeneration; image: SourceImage } }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/images`, { prompt, checkpointId });
  },

  getCharacterImages: async (storyId: string, characterId: string, checkpointId?: string): Promise<{ data: SourceImage[] }> => {
    return apiClient.get<{ data: SourceImage[] }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/images`, { params: { checkpointId } });
  },

  setCharacterAvatar: async (storyId: string, characterId: string, imageId: string, checkpointId?: string): Promise<{ data: SourceCharacter | SourceCharacterCheckpoint }> => {
    return apiClient.put<{ data: SourceCharacter | SourceCharacterCheckpoint }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/avatar`, { imageId, checkpointId });
  },

  deleteCharacterImage: async (storyId: string, characterId: string, imageId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/images/${imageId}`);
  },

  autoGenerateCharacterAvatar: async (storyId: string, characterId: string): Promise<{ data: { prompt: string; image: SourceImage } }> => {
    return apiClient.post<{ data: { prompt: string; image: SourceImage } }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/auto-avatar`);
  },

  suggestCharacterName: async (storyId: string, characterId: string, data?: { name?: string; gender?: string; description?: string }): Promise<{ data: { suggestedName: string } }> => {
    return apiClient.post<{ data: { suggestedName: string } }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/suggest-name`, data);
  },

  getCharacterGenerations: async (storyId: string, characterId: string, checkpointId?: string): Promise<{ data: SourceImageGeneration[] }> => {
    return apiClient.get<{ data: SourceImageGeneration[] }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/generations`, { params: { checkpointId } });
  },

  uploadCharacterImage: async (storyId: string, characterId: string, file: File, checkpointId?: string): Promise<{ data: { image: SourceImage; character?: SourceCharacter; checkpoint?: SourceCharacterCheckpoint } }> => {
    const formData = new FormData();
    formData.append('image', file);
    if (checkpointId) {
      formData.append('checkpointId', checkpointId);
    }
    return apiClient.upload<{ data: { image: SourceImage; character?: SourceCharacter; checkpoint?: SourceCharacterCheckpoint } }>(`/api/v1/admin/source-stories/${storyId}/characters/${characterId}/images/upload`, formData);
  },

  // Panel Image APIs
  generatePanelImage: async (storyId: string, sceneId: string, panelId: string, prompt: string | null): Promise<{ data: { generation: SourceImageGeneration; image: SourceImage } }> => {
    return apiClient.post<{ data: { generation: SourceImageGeneration; image: SourceImage } }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/images`, { prompt });
  },

  refinePanelVisualPrompt: async (storyId: string, sceneId: string, panelId: string): Promise<{ data: { refinedPrompt: string; technicalPrompt: string; motionPrompt?: string } }> => {
    return apiClient.post<{ data: { refinedPrompt: string; technicalPrompt: string; motionPrompt?: string } }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/refine-prompt`);
  },

  getPanelImages: async (storyId: string, sceneId: string, panelId: string): Promise<{ data: SourceImage[] }> => {
    return apiClient.get<{ data: SourceImage[] }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/images`);
  },

  setPanelImage: async (storyId: string, sceneId: string, panelId: string, imageId: string): Promise<{ data: SourcePanel }> => {
    return apiClient.put<{ data: SourcePanel }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/image`, { imageId });
  },

  deletePanelImage: async (storyId: string, sceneId: string, panelId: string, imageId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/images/${imageId}`);
  },

  uploadPanelImage: async (storyId: string, sceneId: string, panelId: string, file: File): Promise<{ data: { image: SourceImage; panel: SourcePanel } }> => {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.upload<{ data: { image: SourceImage; panel: SourcePanel } }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/images/upload`, formData);
  },

  // Preview AI decision for video generation (multi-shot vs single-shot)
  previewVideoGenerationDecision: async (storyId: string, sceneId: string, panelId: string): Promise<{ success: boolean; data: any }> => {
    return apiClient.get<{ success: boolean; data: any }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/video-generation-preview`);
  },

  generatePanelVideo: async (
    storyId: string, 
    sceneId: string, 
    panelId: string, 
    options?: { forceMode?: 'single' | 'multi'; forceDuration?: number; regeneratePrompt?: boolean }
  ): Promise<{ success: boolean; data: { generationId: string; status: string; mode: string; estimatedDuration: number; reasoning: string } }> => {
    return apiClient.post<{ success: boolean; data: { generationId: string; status: string; mode: string; estimatedDuration: number; reasoning: string } }>(
      `/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/generate-video`,
      options || {}
    );
  },

  getAiVideoGeneration: async (generationId: string): Promise<{ success: boolean; data: any }> => {
    return apiClient.get<{ success: boolean; data: any }>(`/api/v1/admin/ai-video-generations/${generationId}`);
  },

  getVideoHistory: async (panelId: string): Promise<{ success: boolean; data: any[] }> => {
    return apiClient.get<{ success: boolean; data: any[] }>(`/api/v1/admin/ai-video-generations/owner/PANEL/${panelId}`);
  },

  applyVideoFromHistory: async (storyId: string, sceneId: string, panelId: string, generationId: string): Promise<{ success: boolean; data: SourcePanel }> => {
    return apiClient.post<{ success: boolean; data: SourcePanel }>(
      `/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/apply-video`,
      { generationId }
    );
  },

  extractPanelEndFrame: async (storyId: string, sceneId: string, panelId: string): Promise<{ data: SourcePanel }> => {
    return apiClient.post<{ data: SourcePanel }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/extract-end-frame`);
  },

  checkVisualReferences: async (storyId: string, sceneId: string, panelId: string): Promise<{ data: any }> => {
    return apiClient.post<{ data: any }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/check-reference`);
  },

  addManualReference: async (storyId: string, sceneId: string, panelId: string, sourcePanelId: string): Promise<{ data: any }> => {
    return apiClient.post<{ data: any }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/manual-reference`, { sourcePanelId });
  },

  removeReference: async (storyId: string, sceneId: string, panelId: string, sourceId: string): Promise<{ data: any }> => {
    return apiClient.delete<{ data: any }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/references/${sourceId}`);
  },

  // Story Voice APIs
  getStoryVoices: async (storyId: string): Promise<{ data: StoryVoice[] }> => {
    return apiClient.get<{ data: StoryVoice[] }>(`/api/v1/admin/source-stories/${storyId}/voices`);
  },
  
  activateStoryVoice: async (storyId: string, voice: SampleStoryVoice): Promise<{ data: StoryVoice }> => {
    return apiClient.post<{ data: StoryVoice }>(`/api/v1/admin/source-stories/${storyId}/voices`, { voice });
  },

  // Panel Audio APIs
  generatePanelAudio: async (storyId: string, sceneId: string, panelId: string, content: string): Promise<{ data: SourcePanelAudio }> => {
    return apiClient.post<{ data: SourcePanelAudio }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/audio`, { prompt: content });
  },

  getPanelAudios: async (storyId: string, sceneId: string, panelId: string): Promise<{ data: SourcePanelAudio[] }> => {
    return apiClient.get<{ data: SourcePanelAudio[] }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/audio`);
  },

  setPanelAudioStatus: async (storyId: string, sceneId: string, panelId: string, audioId: string, status: 'active' | 'inactive'): Promise<{ data: SourcePanelAudio }> => {
    return apiClient.put<{ data: SourcePanelAudio }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/audio/${audioId}/status`, { status });
  },
  
  deletePanelAudio: async (storyId: string, sceneId: string, panelId: string, audioId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/scenes/${sceneId}/panels/${panelId}/audio/${audioId}`);
  },

  // Source Glossary APIs
  getSourceGlossaries: async (storyId: string, params?: GetSourceGlossaryParams): Promise<PaginatedResponse<SourceGlossary>> => {
    return apiClient.get<PaginatedResponse<SourceGlossary>>(`/api/v1/admin/source-stories/${storyId}/glossaries`, { params });
  },

  createSourceGlossary: async (storyId: string, data: CreateSourceGlossaryRequest): Promise<{ data: SourceGlossary }> => {
    return apiClient.post<{ data: SourceGlossary }>(`/api/v1/admin/source-stories/${storyId}/glossaries`, data);
  },

  updateSourceGlossary: async (storyId: string, glossaryId: string, data: UpdateSourceGlossaryRequest): Promise<{ data: SourceGlossary }> => {
    return apiClient.put<{ data: SourceGlossary }>(`/api/v1/admin/source-stories/${storyId}/glossaries/${glossaryId}`, data);
  },

  deleteSourceGlossary: async (storyId: string, glossaryId: string): Promise<{ data: {} }> => {
    return apiClient.delete<{ data: {} }>(`/api/v1/admin/source-stories/${storyId}/glossaries/${glossaryId}`);
  },
};
